import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Row, Col, Card, CardTitle, CardBody, CardFooter } from "reactstrap";
import moment from "moment";

import api from "../../../../../services/api";
import { useCompanies } from "../../../../../hooks/companies";
import { useDateFilter } from "../../../../../hooks/datefilter";
import { paramsOcorrenciasAbertas } from "../ocorrenciasAbertasParams";
import "../../indicadoresLinha.css";

// KPIs de entrega da aba "Performance de Entrega" (PO1-2819).
//
// Espelha o `componentsCusto/IndicadoresFrete` — mesmo formato de card, mesmo
// contrato `deferLoad`/`onRegisterFetch` de orquestração da página. NÃO segue os
// 4 charts antigos desta aba, que ignoram esse contrato.
//
// Duas fontes de propósito: os cards de status saem de
// /dashboard/performance/entregas-kpi (regra canônica da PO1-2820) e o card de
// ocorrências sai de /dashboard/performance/ocorrencias-abertas, que é um
// indicador independente, de outro domínio. Não somar um no outro.

const DIAS_PADRAO = 365;
const ROTA_PENDENTES = "/app/trackfy/operacao/entregas-pendentes";

const fmtNumero = (v) =>
  v === null || v === undefined ? "-" : Number(v).toLocaleString("pt-BR");

const fmtPercent = (v) =>
  v === null || v === undefined
    ? "-"
    : `${Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

export default function IndicadoresEntrega({ deferLoad = false, onRegisterFetch }) {
  const [indicadores, setIndicadores] = useState([]);
  const { empresa, transportadora, arrayParams } = useCompanies();
  const { startDate, endDate } = useDateFilter();
  const history = useHistory();
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadIndicadores = useCallback(async () => {
    const inicio = startDate
      ? moment(startDate)
      : moment().subtract(DIAS_PADRAO, "days");
    const fim = endDate ? moment(endDate) : moment();
    const dataInicial = inicio.format("YYYY-MM-DD 00:00:00");
    const dataFinal = fim.format("YYYY-MM-DD 23:59:59");

    const params = { dataInicial, dataFinal };
    if (arrayParams.transportadoras) {
      params.transportadoras = arrayParams.transportadoras;
    }

    // O drill-down precisa cair no MESMO recorte do card, senão o número da
    // lista não bate com o do indicador — critério de aceite da história.
    const linkPendentes = (status) => {
      const filtro = status ? `&status=${status}` : "";
      return `${ROTA_PENDENTES}?dataInicial=${encodeURIComponent(
        dataInicial
      )}&dataFinal=${encodeURIComponent(dataFinal)}${filtro}`;
    };

    try {
      const [kpiRes, ocorrenciasRes] = await Promise.all([
        api.get("/dashboard/performance/entregas-kpi", { params }),
        // PO1-3214: mesmo recorte do gráfico "Ocorrências em aberto".
        api
          .get("/dashboard/performance/ocorrencias-abertas", {
            params: paramsOcorrenciasAbertas({
              startDate,
              endDate,
              transportadoras: arrayParams.transportadoras,
            }),
          })
          .catch(() => null),
      ]);

      const cards = [];

      if (kpiRes && kpiRes.status === 200) {
        const kpi = kpiRes.data;

        cards.push({
          titulo: "Entregas no período",
          valor: fmtNumero(kpi.total),
          subtitulo: `${fmtNumero(kpi.emAberto)} ainda em aberto`,
          icone: "tim-icons icon-delivery-fast",
          tom: "icon-primary",
          link: linkPendentes(null),
        });

        // Pontualidade sobre quem TEM previsão. A base vai no subtítulo porque
        // hoje boa parte das entregas concluídas não tem previsão cadastrada —
        // mostrar o percentual sem dizer a base seria enganoso (épico PO1-2886).
        cards.push({
          titulo: "Entregas no prazo",
          valor: fmtPercent(kpi.percentualNoPrazo),
          subtitulo:
            kpi.baseNoPrazo > 0
              ? `${fmtNumero(kpi.entreguesNoPrazo)} de ${fmtNumero(
                  kpi.baseNoPrazo
                )} entregas com previsão`
              : "Nenhuma entrega concluída tem previsão para comparar",
          icone: "tim-icons icon-check-2",
          tom: "icon-success",
        });

        cards.push({
          titulo: "Atrasadas",
          valor: fmtNumero(kpi.atrasadas),
          subtitulo: "Previsão vencida, sem entrega registrada",
          icone: "tim-icons icon-alert-circle-exc",
          tom: "icon-danger",
          link: linkPendentes("atrasada"),
        });

        // Subconjunto das atrasadas sem NENHUMA ocorrência da transportadora —
        // o embarcador está no escuro. É o número que a gestão de entregas
        // precisa ver primeiro (Rodrigo, QA de 03/09).
        cards.push({
          titulo: "Atrasadas sem retorno",
          valor: fmtNumero(kpi.atrasadasSemRetorno),
          subtitulo: "Vencidas e sem nenhuma ocorrência da transportadora",
          icone: "tim-icons icon-bulb-63",
          tom: "icon-danger",
          link: `${linkPendentes("atrasada")}&semRetorno=1`,
        });

        const semPrevisao =
          (kpi.porStatus || []).find((item) => item.status === "sem_previsao") || {};

        cards.push({
          titulo: "Sem previsão",
          valor: fmtNumero(semPrevisao.quantidade),
          subtitulo: `${fmtNumero(
            kpi.entreguesSemPrevisao
          )} entregas concluídas também sem previsão`,
          icone: "tim-icons icon-calendar-60",
          tom: "icon-warning",
          link: linkPendentes("sem_previsao"),
        });

        cards.push({
          titulo: "Devolvidas",
          valor: fmtNumero(kpi.devolvidas),
          subtitulo: "Mercadoria retornada ao remetente",
          icone: "tim-icons icon-refresh-02",
          tom: "icon-warning",
        });
      }

      // Indicador independente, de outro domínio: não entra nas contas acima.
      if (ocorrenciasRes && ocorrenciasRes.status === 200) {
        const total = (ocorrenciasRes.data || []).reduce(
          (soma, item) => soma + Number(item.qtd || 0),
          0
        );

        cards.push({
          titulo: "Ocorrências abertas",
          valor: fmtNumero(total),
          subtitulo: "NFs não entregues com ocorrência pendente",
          icone: "tim-icons icon-bell-55",
          tom: "icon-info",
        });
      }

      if (isMounted.current) setIndicadores(cards);
    } catch (error) {
      if (isMounted.current) {
        console.error("Erro ao carregar indicadores de entrega:", error);
        setIndicadores([]);
      }
    }
  }, [arrayParams.transportadoras, startDate, endDate]);

  useEffect(() => {
    if (onRegisterFetch) {
      onRegisterFetch(loadIndicadores);
    }

    if (!deferLoad) {
      loadIndicadores();
    }
  }, [deferLoad, empresa, loadIndicadores, onRegisterFetch, transportadora]);

  return (
    <Row className="indicadores-linha">
      {indicadores.map((indicador) => (
        <Col lg="3" md="6" key={indicador.titulo}>
          <Card
            style={{
              boxShadow: "0px 1px 20px 0px rgb(0, 0, 0)", // theme-ok: mesma sombra dos demais cards de indicador
              cursor: indicador.link ? "pointer" : "default",
            }}
            className="card-stats"
            onClick={indicador.link ? () => history.push(indicador.link) : undefined}
          >
            <CardBody>
              <Row>
                <Col xs="4">
                  <div className={`info-icon text-center ${indicador.tom}`}>
                    <i className={indicador.icone} />
                  </div>
                </Col>
                <Col xs="8">
                  <div className="numbers">
                    <CardTitle tag="h4">{indicador.titulo}</CardTitle>
                    <CardTitle tag="h3">{indicador.valor}</CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <hr />
              <div
                className="stats"
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <i className="tim-icons icon-sound-wave" />
                {indicador.subtitulo}
              </div>
            </CardFooter>
          </Card>
        </Col>
      ))}
    </Row>
  );
}
